import { BehaviorSubject, Observable, map, first } from 'rxjs';
import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { GroupI, MessageGroupI, NotificationGroupsCountI, NotificationGroupsI } from '@interfaces/group.interface';
import * as moment from '@node_modules/moment';
import { cloneArray } from '@node_modules/jssip/lib/Utils';
import { firstValueFrom } from '@node_modules/rxjs';
import { MessageStatusE, SenderTypeE } from '@enums/message.enum';
import { PageI } from '@interfaces/page.interface';
import { TranscriptionI } from '@interfaces/transcription.interface';
import { ChannelE } from '@interfaces/channel.interface';
import { UserHelperService } from '@helpers/user-helper.service';

export interface MessageStore {
  messages: { [id: string]: PageI<MessageGroupI> };
  scrollPositions: { [id: string]: number };
  notificationGroups: NotificationGroupsI;
  currentGroupId: string;
}

@Injectable({
  providedIn: 'root',
})
export class MessageGroupStoreService extends ComponentStore<MessageStore> {
  public messages$: Observable<{ [id: string]: PageI<MessageGroupI> }> = this.select((state) => state.messages);
  public scrollPositions$: Observable<{ [id: string]: number }> = this.select((state) => state.scrollPositions);
  private messageAddedSubjects: { [id: string]: BehaviorSubject<MessageGroupI | undefined> } = {};
  public completeSelectedMessages: MessageGroupI[] = [];

  public notificationTotalGroups$: Observable<number> = this.select((state) => state.notificationGroups.totals);
  public notificationGroups$: Observable<NotificationGroupsI> = this.select((state) => state.notificationGroups);

  public notificationWhatsappTotal$: Observable<number> = this.getNotificationTotalsFromType(ChannelE.WHATSAPP);
  public notificationInternalTotal$: Observable<number> = this.getNotificationTotalsFromType(ChannelE.INTERNAL);

  constructor(private readonly userHelperService: UserHelperService) {
    super({
      messages: {},
      scrollPositions: {},
      notificationGroups: { groups: [], totals: 0 },
      currentGroupId: '',
    });
  }

  private getNotificationTotalsFromType(type: ChannelE) {
    return this.select((state) => {
      let count = 0;
      for (const notificationGroup of state.notificationGroups?.groups ?? []) {
        if (notificationGroup.type === type) {
          count += notificationGroup.count;
        }
      }

      return count;
    });
  }

  public getScrollPosition(groupId: string): Observable<number> {
    return this.scrollPositions$.pipe(map((scrollPositions) => scrollPositions[groupId]));
  }

  public setCurrentGroupId(groupId: string): void {
    this.setState((state) => ({
      ...state,
      currentGroupId: groupId,
      notificationGroups: this.viewNotification(groupId, state.notificationGroups),
    }));
  }

  public setScrollPosition(groupId: string, position: number) {
    this.setState((state) => ({
      ...state,
      scrollPositions: {
        ...state.scrollPositions,
        [groupId]: position,
      },
    }));
  }

  public async populateStore(groupId: string, page: PageI<MessageGroupI>, addOnlyNotExists: boolean = false) {
    this.setState((state) => {
      if (addOnlyNotExists) {
        if (!state?.messages[groupId]) {
          return {
            ...state,
            messages: {
              ...state.messages,
              [groupId]: page,
            },
          };
        }
        return state;
      }
      return {
        ...state,
        messages: {
          ...state.messages,
          [groupId]: page,
        },
      };
    });
    this.updateNotification();
  }

  public unshiftMessagesByChatId(groupId: string, page: PageI<MessageGroupI>) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [groupId]: {
          data: [...page.data, ...state.messages[groupId].data],
          meta: page.meta,
        },
      },
    }));

    this.updateNotification();
  }

  public pushMessagesByChatId(groupId: string, page: PageI<MessageGroupI>) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [groupId]: {
          data: [...state.messages[groupId].data, ...page.data],
          meta: page.meta,
        },
      },
    }));

    this.updateNotification();
  }

  public populateOrUnshiftMessagesByChatId(groupId: string, page: PageI<MessageGroupI>) {
    if (page.meta.page === 1 && page.meta.initialLazyLoadMessages) {
      void this.populateStore(groupId, page);
    } else {
      this.unshiftMessagesByChatId(groupId, page);
    }
  }

  public addMessage(groupId: string, message: MessageGroupI) {
    this.setState((state) => {
      //if message is from current group, mark as read
      if (state.currentGroupId === groupId && message?.sender?.type === 'contact') {
        message.status = MessageStatusE.READ;
      }

      return {
        ...state,
        messages: {
          ...state.messages,
          [groupId]: {
            data: state.messages[groupId]?.data ? [...state.messages[groupId].data, message] : [message],
            meta: state.messages[groupId]?.meta || null,
          },
        },
      };
    });

    if (!this.messageAddedSubjects[groupId]) {
      this.messageAddedSubjects[groupId] = new BehaviorSubject<MessageGroupI | undefined>(undefined);
    }
    this.messageAddedSubjects[groupId].next(message);
    this.updateNotification();
  }

  public updateMessageById(groupId: string, messageId: string, updatedMessage: MessageGroupI) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [groupId]: {
          data: state.messages[groupId]?.data?.map((message) => (message._id === messageId ? updatedMessage : message)),
          meta: state.messages[groupId]?.meta || null,
        },
      },
    }));
  }

  public async getMessageById(groupId: string, messageId: string) {
    const groupMessages = await firstValueFrom(this.getMessagesByChatId(groupId));
    return groupMessages.data?.find((message) => message._id === messageId);
  }

  public getMessagesByChatId(chatId: string): Observable<PageI<MessageGroupI>> {
    return this.messages$.pipe(map((messages) => messages[chatId]));
  }

  public getMessageAddedSubject(groupId: string): Observable<MessageGroupI | undefined> {
    if (!this.messageAddedSubjects[groupId]) {
      this.messageAddedSubjects[groupId] = new BehaviorSubject<MessageGroupI | undefined>(undefined);
    }
    return this.messageAddedSubjects[groupId].asObservable();
  }

  public updateAllMessageStatusByChat(groupId: string, status: MessageStatusE, updatingAgentMessages: boolean = false) {
    this.setState((state) => {
      return {
        ...state,
        messages: {
          ...state.messages,
          [groupId]: {
            data: state.messages[groupId]?.data?.map((message) => {
              if (status === 'read') {
                if (message?.sender?.type === SenderTypeE.CONTACT) {
                  message.status = MessageStatusE.READ;
                }
                if (updatingAgentMessages) {
                  message.status = status;
                }
              } else {
                message.status = status;
              }
              return message;
            }),
            meta: state.messages[groupId]?.meta || null,
          },
        },
      };
    });
    this.updateNotification();
  }

  public updateLastMessageStatusByChat(groupId: string, status: MessageStatusE) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [groupId]: {
          data: state.messages[groupId]?.data?.map((message, index) => {
            if (state.messages[groupId]?.data?.length - 1 === index) {
              message.status = status;
            }
            return message;
          }),
          meta: state.messages[groupId]?.meta || null,
        },
      },
    }));
  }

  public updateMessageDeleteStatusById(groupId: string, messageId: string) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [groupId]: {
          data: state.messages[groupId]?.data?.map((message) => {
            if (message._id === messageId) {
              message.deletedMessage = true;
            }
            return message;
          }),
          meta: state.messages[groupId]?.meta || null,
        },
      },
    }));
  }

  public updateMessageTranscriptionById(groupId: string, messageId: string, transcription: TranscriptionI) {
    this.setState((state) => ({
      ...state,
      messages: {
        ...state.messages,
        [groupId]: {
          data: state.messages[groupId]?.data?.map((message) => {
            if (message._id === messageId) {
              message.transcription = transcription;
            }
            return message;
          }),
          meta: state.messages[groupId]?.meta || null,
        },
      },
    }));
  }

  public updateMessageId(groupId: string, tmpMessageId: string, messageId: string) {
    this.setState((state) => {
      const messages =
        state.messages[groupId]?.data?.map((msg) => {
          if (msg._id === tmpMessageId) {
            return { ...msg, _id: messageId };
          }
          return msg;
        }) || [];

      return {
        ...state,
        messages: {
          ...state.messages,
          [groupId]: {
            data: messages,
            meta: state.messages[groupId]?.meta || null,
          },
        },
      };
    });
  }

  async getNotification(groupId: string) {
    const notifications = await firstValueFrom(this.notificationGroups$);

    return notifications.groups.find((value) => value._id === groupId);
  }

  updateNotification() {
    this.setState((state) => ({
      ...state,
      notificationGroups: this.getNotificationsCount(state.messages, state.currentGroupId),
    }));
  }

  clearNotification() {
    this.setState((state) => ({
      ...state,
      notificationGroups: { ...state.notificationGroups, totals: 0 },
    }));
  }

  private getNotificationsCount(
    messages: { [id: string]: PageI<MessageGroupI> },
    currentGroupId: string
  ): NotificationGroupsI {
    let groups: NotificationGroupsCountI[] = [];
    for (const messagesKey in messages) {
      const messagesData = messages[messagesKey].data;
      const count = this.countMessageAgentWithDeliveries(messagesData);

      groups.push({
        _id: messagesKey,
        count,
        type: messagesData ? messagesData[0]?.channelType : ChannelE.INTERNAL,
      });
    }

    groups = groups.filter((value) => value._id !== currentGroupId && value.count > 0);

    return {
      groups,
      totals: groups.length,
    };
  }

  private countMessageAgentWithDeliveries(messages: MessageGroupI[]): number {
    let count = 0;

    if (!messages) {
      return count;
    }

    for (const message of cloneArray(messages).reverse()) {
      if (
        (message.channelType === ChannelE.WHATSAPP &&
          message?.sender?.type === SenderTypeE.CONTACT &&
          message?.status !== 'read') ||
        (message.channelType === ChannelE.INTERNAL &&
          message?.status === 'delivered' &&
          !this.userHelperService.verifyIfUserIsCurrentUser(message?.sender?.senderId))
      ) {
        count++;
      }
    }

    return count;
  }

  private viewNotification(groupId: string, notifications: NotificationGroupsI) {
    notifications.groups = notifications.groups.filter((value) => value._id !== groupId);
    notifications.totals = notifications.groups.length;

    return notifications;
  }

  private viewMessage(groupId: string, groups: GroupI[]) {
    return groups.map((group) => {
      if (group._id === groupId && group.messages) {
        return {
          ...group,
          messages: group.messages.map((message) => {
            if (message?.sender?.type === SenderTypeE.CONTACT && message.status === 'delivered') {
              message.status = MessageStatusE.READ;
            }
            return message;
          }),
        };
      }
      return group;
    });
  }
}
